import { useState, useEffect, useContext, useRef } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import {
  FolderKanban, Calendar, User, Camera, Plus, Eye, ChevronRight, Upload, X, CheckCircle2,
  Save, History, Pencil, Paperclip, FileText, Trash2, Loader2,
} from 'lucide-react';
import ProjectHistoryModal from '@/components/ProjectHistoryModal';
import PhasesEditor from '@/components/PhasesEditor';
import { canUserEdit, canUserDelete } from '@/utils/permissions';
import { useProjectDirectory } from '@/hooks/useProjectDirectory';
import { DuplicateProjectWarning } from '@/components/DuplicateProjectWarning';

const PAGE_PATH = '/projects/ongoing';

const emptyProjectForm = {
  name: '',
  description: '',
  projectIncharge: '',
  startDate: '',
  deadline: '',
  estimatedCost: '',
  phases: [],
};

const emptyStatusForm = {
  name: '',
  description: '',
  projectIncharge: '',
  startDate: '',
  deadline: '',
  estimatedCost: '',
  actualCost: '',
  status: 'Ongoing',
  progress: 0,
  remarks: '',
  phases: [],
};

function OngoingProjects() {
  const { user, API } = useContext(AppContext);
  const canEdit = canUserEdit(user, PAGE_PATH);
  const canDelete = canUserDelete(user, PAGE_PATH);

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadingProject, setUploadingProject] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadCaption, setUploadCaption] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const projectDirectory = useProjectDirectory({ includeCompleted: true, enabled: showAddModal });
  const [addForm, setAddForm] = useState(emptyProjectForm);
  const [newProjectDocs, setNewProjectDocs] = useState([]); // queued before create
  const [submitting, setSubmitting] = useState(false);
  const [completingId, setCompletingId] = useState(null);

  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusProject, setStatusProject] = useState(null);
  const [statusForm, setStatusForm] = useState(emptyStatusForm);
  const [statusDocs, setStatusDocs] = useState([]); // pending new docs for this update
  const [savingStatus, setSavingStatus] = useState(false);
  const [docDeletingId, setDocDeletingId] = useState(null);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyProject, setHistoryProject] = useState(null);

  const fileInputRef = useRef(null);
  const addDocsInputRef = useRef(null);
  const statusDocsInputRef = useRef(null);

  useEffect(() => { fetchProjects(); }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects/ongoing`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const viewProjectDetails = async (projectId) => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}`);
      setSelectedProject(response.data);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Error fetching project details:', error);
    }
  };

  // ---------------- NEW PROJECT ----------------
  const handleAddProject = async (e) => {
    e.preventDefault();
    if (!addForm.name.trim()) { alert('Project name is required.'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/projects`, {
        ...addForm,
        estimatedCost: parseFloat(addForm.estimatedCost) || 0,
        phases: (addForm.phases || []).filter((p) => p.name?.trim()).map((p) => ({
          id: p.id,
          name: p.name.trim(),
          weight: parseFloat(p.weight) || 0,
          progress: parseFloat(p.progress) || 0,
        })),
      });
      const created = res.data;
      // Upload any queued documents
      if (newProjectDocs.length > 0 && created?.id) {
        const fd = new FormData();
        newProjectDocs.forEach((f) => fd.append('files', f));
        try {
          await axios.post(`${API}/projects/${created.id}/documents`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch (err) {
          alert('Project created, but some documents failed to upload: ' +
            (err?.response?.data?.detail || err.message));
        }
      }
      setShowAddModal(false);
      setAddForm(emptyProjectForm);
      setNewProjectDocs([]);
      fetchProjects();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to create project');
    } finally {
      setSubmitting(false);
    }
  };

  const queueNewProjectDocs = (e) => {
    const files = Array.from(e.target.files || []);
    setNewProjectDocs((prev) => [...prev, ...files]);
    e.target.value = '';
  };

  const removeQueuedDoc = (idx, listSetter) => {
    listSetter((prev) => prev.filter((_, i) => i !== idx));
  };

  // ---------------- COMPLETE / STATUS ----------------
  const handleCompleteProject = async (project) => {
    if (!window.confirm(`Mark "${project.name}" as completed? It will move to the Completed Projects page.`)) return;
    setCompletingId(project.id);
    try {
      await axios.post(`${API}/projects/${project.id}/complete`);
      fetchProjects();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to complete project');
    } finally {
      setCompletingId(null);
    }
  };

  const openStatusModal = (project) => {
    setStatusProject(project);
    setStatusForm({
      name: project.name || '',
      description: project.description || '',
      projectIncharge: project.projectIncharge || '',
      startDate: project.startDate || '',
      deadline: project.deadline || '',
      estimatedCost: project.estimatedCost ?? '',
      actualCost: project.actualCost ?? '',
      status: project.status || 'Ongoing',
      progress: Number(project.progress || 0),
      remarks: project.remarks || '',
      phases: (project.phases || []).map((p) => ({
        id: p.id || (crypto.randomUUID ? crypto.randomUUID() : String(Math.random())),
        name: p.name || '',
        weight: p.weight ?? '',
        progress: Number(p.progress || 0),
      })),
    });
    setStatusDocs([]);
    setShowStatusModal(true);
  };

  const queueStatusDocs = (e) => {
    const files = Array.from(e.target.files || []);
    setStatusDocs((prev) => [...prev, ...files]);
    e.target.value = '';
  };

  const handleSaveStatus = async (e) => {
    e.preventDefault();
    if (!statusProject) return;
    if (!statusForm.name?.trim()) { alert('Project name is required.'); return; }
    setSavingStatus(true);
    try {
      const cleanPhases = (statusForm.phases || [])
        .filter((p) => p.name?.trim())
        .map((p) => ({
          id: p.id,
          name: p.name.trim(),
          weight: parseFloat(p.weight) || 0,
          progress: parseFloat(p.progress) || 0,
        }));
      await axios.put(`${API}/projects/${statusProject.id}`, {
        name: statusForm.name.trim(),
        description: statusForm.description,
        projectIncharge: statusForm.projectIncharge,
        startDate: statusForm.startDate,
        deadline: statusForm.deadline,
        estimatedCost: parseFloat(statusForm.estimatedCost) || 0,
        actualCost: parseFloat(statusForm.actualCost) || 0,
        status: statusForm.status,
        // If phases exist, progress is auto-computed server-side from them.
        // Otherwise fall back to whatever the user typed.
        progress: cleanPhases.length > 0
          ? undefined
          : Math.max(0, Math.min(100, Number(statusForm.progress) || 0)),
        remarks: statusForm.remarks,
        phases: cleanPhases,
      });

      if (statusDocs.length > 0) {
        const fd = new FormData();
        statusDocs.forEach((f) => fd.append('files', f));
        await axios.post(`${API}/projects/${statusProject.id}/documents`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      setShowStatusModal(false);
      setStatusProject(null);
      setStatusDocs([]);
      fetchProjects();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to save changes');
    } finally {
      setSavingStatus(false);
    }
  };

  const handleDeleteProject = async (project) => {
    if (!canDelete) return;
    if (!window.confirm(`Delete project "${project.name}"? This will remove all its documents and history. This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/projects/${project.id}`);
      setProjects((prev) => prev.filter((p) => p.id !== project.id));
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to delete project');
    }
  };

  const handleDeleteDoc = async (projectId, doc) => {
    if (!canDelete) return;
    if (!window.confirm(`Delete document "${doc.name || doc.filename}"?`)) return;
    setDocDeletingId(doc.id);
    try {
      await axios.delete(`${API}/projects/${projectId}/documents/${doc.id}`);
      // Update local state
      setProjects((prev) => prev.map((p) =>
        p.id === projectId
          ? { ...p, documents: (p.documents || []).filter((d) => d.id !== doc.id) }
          : p
      ));
      if (statusProject?.id === projectId) {
        setStatusProject((p) => p ? { ...p, documents: (p.documents || []).filter((d) => d.id !== doc.id) } : p);
      }
      if (selectedProject?.id === projectId) {
        setSelectedProject((p) => p ? { ...p, documents: (p.documents || []).filter((d) => d.id !== doc.id) } : p);
      }
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to delete document');
    } finally {
      setDocDeletingId(null);
    }
  };

  // ---------------- IMAGE UPLOAD ----------------
  const openUploadModal = (project) => { setUploadingProject(project); setShowUploadModal(true); };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', 'project');
    formData.append('reference_id', uploadingProject.id);
    setUploading(true);
    try {
      await axios.post(`${API}/upload/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      alert('Photo uploaded successfully!');
      setShowUploadModal(false);
      setUploadCaption('');
      fetchProjects();
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload photo. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // ---------------- HELPERS ----------------
  const getProgressColor = (progress) => {
    if (progress >= 75) return 'bg-green-500';
    if (progress >= 50) return 'bg-blue-500';
    if (progress >= 25) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getPhaseStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-700';
      case 'In Progress': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const formatSize = (b) => {
    if (!b && b !== 0) return '';
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(2)} MB`;
  };

  if (loading) return <div className="text-center py-12">Loading projects...</div>;

  return (
    <div className="space-y-6" data-testid="ongoing-projects-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ongoing Projects</h1>
          <p className="text-gray-500 mt-1">Track active project progress and deadlines</p>
        </div>
        {canEdit && (
          <button
            onClick={() => { setAddForm(emptyProjectForm); setNewProjectDocs([]); setShowAddModal(true); }}
            className="bg-gradient-to-r from-[#1a237e] to-[#283593] text-white px-4 py-2.5 rounded-xl text-sm font-medium flex items-center space-x-2 shadow-md hover:opacity-90 transition"
            data-testid="new-project-btn"
          >
            <Plus size={18} />
            <span>New Project</span>
          </button>
        )}
      </div>

      {/* Project Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {projects.map((project) => (
          <div key={project.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow" data-testid={`project-card-${project.id}`}>
            <div className="p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs font-mono text-[#1a237e] bg-indigo-50 px-2 py-1 rounded">{project.id}</span>
                    <span className="text-xs text-gray-500">{project.progress}% Complete</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mt-2">{project.name}</h3>
                  <p className="text-sm text-gray-500 mt-1 line-clamp-2">{project.description}</p>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => { setHistoryProject(project); setHistoryOpen(true); }}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Project Timeline"
                    data-testid={`history-project-${project.id}`}
                  >
                    <History size={18} className="text-gray-500" />
                  </button>
                  <button
                    onClick={() => viewProjectDetails(project.id)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    data-testid={`view-project-${project.id}`}
                  >
                    <Eye size={18} className="text-gray-500" />
                  </button>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mt-4">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className={`h-2 rounded-full ${getProgressColor(project.progress)} transition-all`}
                       style={{ width: `${project.progress}%` }} />
                </div>
              </div>

              {/* Project Meta */}
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-2 text-sm">
                  <User size={14} className="text-gray-400" />
                  <span className="text-gray-600">{project.projectIncharge}</span>
                </div>
                <div className="flex items-center space-x-2 text-sm">
                  <Calendar size={14} className="text-gray-400" />
                  <span className="text-gray-600">Due: {project.deadline}</span>
                </div>
              </div>

              {/* Budget */}
              <div className="mt-4 flex items-center justify-between text-sm">
                <span className="text-gray-500">Budget:</span>
                <span className="font-semibold text-gray-900">
                  ₹{(project.actualCost / 100000).toFixed(1)}L / ₹{(project.estimatedCost / 100000).toFixed(1)}L
                </span>
              </div>

              {/* Phases Preview */}
              {project.phases?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {project.phases.slice(0, 3).map((phase, idx) => (
                    <span key={idx} className={`px-2 py-1 rounded text-xs font-medium ${getPhaseStatusColor(phase.status)}`}>
                      {phase.name}
                    </span>
                  ))}
                  {project.phases.length > 3 && (
                    <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600">
                      +{project.phases.length - 3} more
                    </span>
                  )}
                </div>
              )}

              {/* Documents Preview */}
              {project.documents?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-gray-600 flex items-center gap-1">
                      <Paperclip size={12} />
                      Documents ({project.documents.length})
                    </span>
                  </div>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {project.documents.slice(0, 4).map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between text-xs bg-gray-50 rounded px-2 py-1.5 group">
                        <a href={`${process.env.REACT_APP_BACKEND_URL}${doc.url}`} target="_blank" rel="noreferrer"
                           className="flex items-center gap-1 text-blue-700 hover:underline truncate flex-1">
                          <FileText size={12} className="shrink-0" />
                          <span className="truncate">{doc.name}</span>
                        </a>
                        <span className="text-gray-400 mx-2 shrink-0">{formatSize(doc.size)}</span>
                        {canDelete && (
                          <button
                            onClick={() => handleDeleteDoc(project.id, doc)}
                            disabled={docDeletingId === doc.id}
                            className="text-rose-600 hover:bg-rose-50 p-1 rounded disabled:opacity-50"
                            data-testid={`delete-doc-${doc.id}`}
                            title="Delete document"
                          >
                            {docDeletingId === doc.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                          </button>
                        )}
                      </div>
                    ))}
                    {project.documents.length > 4 && (
                      <button onClick={() => viewProjectDetails(project.id)}
                              className="text-xs text-[#1a237e] hover:underline mt-1">
                        + {project.documents.length - 4} more — view all
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => openUploadModal(project)}
                    className="text-sm text-[#1a237e] hover:text-[#283593] font-medium flex items-center space-x-1 bg-indigo-50 px-3 py-1.5 rounded-lg hover:bg-indigo-100 transition-colors"
                  >
                    <Camera size={14} />
                    <span>Add Photo</span>
                  </button>
                  {canEdit && (
                    <button
                      onClick={() => openStatusModal(project)}
                      className="text-sm text-blue-700 hover:text-blue-900 font-medium flex items-center space-x-1 bg-blue-50 px-3 py-1.5 rounded-lg hover:bg-blue-100 transition-colors"
                      data-testid={`update-status-${project.id}`}
                    >
                      <Pencil size={14} />
                      <span>Edit</span>
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {canEdit && (
                    <button
                      onClick={() => handleCompleteProject(project)}
                      disabled={completingId === project.id}
                      className="text-sm text-green-700 hover:text-green-900 font-medium flex items-center space-x-1 bg-green-50 px-3 py-1.5 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-60"
                      data-testid={`complete-project-${project.id}`}
                    >
                      <CheckCircle2 size={14} />
                      <span>{completingId === project.id ? 'Completing…' : 'Mark Complete'}</span>
                    </button>
                  )}
                  {canDelete && (
                    <button
                      onClick={() => handleDeleteProject(project)}
                      className="text-sm text-rose-700 hover:text-rose-900 font-medium flex items-center space-x-1 bg-rose-50 px-3 py-1.5 rounded-lg hover:bg-rose-100 transition-colors"
                      data-testid={`delete-project-${project.id}`}
                      title="Delete project"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                  <button
                    onClick={() => viewProjectDetails(project.id)}
                    className="text-sm text-gray-500 hover:text-gray-700 flex items-center space-x-1"
                  >
                    <span>Details</span>
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>

              {/* Images Preview */}
              {project.images?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="flex items-center space-x-2 overflow-x-auto pb-2">
                    {project.images.slice(0, 4).map((img, idx) => (
                      <div key={idx} className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${img.url}`} alt={`Progress ${idx + 1}`}
                             className="w-full h-full object-cover" />
                      </div>
                    ))}
                    {project.images.length > 4 && (
                      <span className="text-xs text-gray-500 px-2">+{project.images.length - 4} more</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {projects.length === 0 && (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <FolderKanban className="mx-auto text-gray-400 mb-3" size={48} />
          <p className="text-gray-500">No ongoing projects yet.</p>
        </div>
      )}

      {/* Project Detail Modal */}
      {showDetailModal && selectedProject && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <span className="text-xs font-mono text-[#1a237e] bg-indigo-50 px-2 py-1 rounded">{selectedProject.id}</span>
                <h2 className="text-xl font-bold text-gray-900 mt-2">{selectedProject.name}</h2>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setHistoryProject(selectedProject); setHistoryOpen(true); }}
                  className="flex items-center gap-1 text-sm text-[#1a237e] bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded-lg"
                  data-testid="detail-history-btn"
                >
                  <History size={14} /> Timeline
                </button>
                <button onClick={() => setShowDetailModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X size={18} /></button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Description</h3>
                <p className="text-gray-600">{selectedProject.description || '—'}</p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-500">Incharge</p><p className="font-semibold text-gray-900">{selectedProject.projectIncharge}</p></div>
                <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-500">Start Date</p><p className="font-semibold text-gray-900">{selectedProject.startDate}</p></div>
                <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-500">Deadline</p><p className="font-semibold text-gray-900">{selectedProject.deadline}</p></div>
                <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-500">Progress</p><p className="font-semibold text-gray-900">{selectedProject.progress}%</p></div>
              </div>

              {/* Documents inside detail modal */}
              {selectedProject.documents?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Paperclip size={16} /> Documents ({selectedProject.documents.length})
                  </h3>
                  <div className="space-y-2">
                    {selectedProject.documents.map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                        <a href={`${process.env.REACT_APP_BACKEND_URL}${doc.url}`} target="_blank" rel="noreferrer"
                           className="flex items-center gap-2 text-sm text-blue-700 hover:underline truncate flex-1">
                          <FileText size={14} className="shrink-0" />
                          <span className="truncate">{doc.name}</span>
                        </a>
                        <span className="text-xs text-gray-400 mx-3 shrink-0">{formatSize(doc.size)}</span>
                        <span className="text-xs text-gray-500 mx-3 shrink-0 hidden sm:block">{doc.uploadedBy || ''}</span>
                        {canDelete && (
                          <button onClick={() => handleDeleteDoc(selectedProject.id, doc)}
                                  disabled={docDeletingId === doc.id}
                                  className="text-rose-600 hover:bg-rose-50 p-1.5 rounded disabled:opacity-50">
                            {docDeletingId === doc.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedProject.images?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Progress Photos ({selectedProject.images.length})</h3>
                  <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                    {selectedProject.images.map((img, idx) => (
                      <div key={idx} className="aspect-square rounded-lg overflow-hidden bg-gray-100 relative group">
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${img.url}`} alt={`Progress ${idx + 1}`} className="w-full h-full object-cover" />
                        <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">{img.uploadDate}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedProject.remarks && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Remarks</h3>
                  <p className="text-gray-600 bg-yellow-50 p-3 rounded-lg">{selectedProject.remarks}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Update Status Modal */}
      {showStatusModal && statusProject && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="status-update-modal">
          <form onSubmit={handleSaveStatus} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-t-2xl">
              <div>
                <h2 className="text-lg font-bold text-white">Edit Project</h2>
                <p className="text-xs text-white/80">{statusProject.name}</p>
              </div>
              <button type="button" onClick={() => setShowStatusModal(false)} className="p-2 text-white/80 hover:bg-white/10 rounded-lg"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Project Name *</label>
                <input type="text" required value={statusForm.name}
                       onChange={(e) => setStatusForm((p) => ({ ...p, name: e.target.value }))}
                       className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                       data-testid="edit-name-input" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Description</label>
                <textarea rows="2" value={statusForm.description}
                          onChange={(e) => setStatusForm((p) => ({ ...p, description: e.target.value }))}
                          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700">Project In-charge</label>
                  <input type="text" value={statusForm.projectIncharge}
                         onChange={(e) => setStatusForm((p) => ({ ...p, projectIncharge: e.target.value }))}
                         className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Estimated Cost (₹)</label>
                  <input type="number" step="0.01" value={statusForm.estimatedCost}
                         onChange={(e) => setStatusForm((p) => ({ ...p, estimatedCost: e.target.value }))}
                         className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700">Start Date</label>
                  <input type="date" value={statusForm.startDate}
                         onChange={(e) => setStatusForm((p) => ({ ...p, startDate: e.target.value }))}
                         className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Deadline</label>
                  <input type="date" value={statusForm.deadline}
                         onChange={(e) => setStatusForm((p) => ({ ...p, deadline: e.target.value }))}
                         className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Actual Cost so far (₹)</label>
                <input type="number" step="0.01" value={statusForm.actualCost}
                       onChange={(e) => setStatusForm((p) => ({ ...p, actualCost: e.target.value }))}
                       className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <hr className="border-gray-200" />
              <div>
                <label className="text-sm font-medium text-gray-700">Status</label>
                <select
                  value={statusForm.status}
                  onChange={(e) => setStatusForm((p) => ({ ...p, status: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                  data-testid="status-select"
                >
                  <option value="Ongoing">Ongoing</option>
                  <option value="On Hold">On Hold</option>
                  <option value="At Risk">At Risk</option>
                </select>
              </div>

              {/* Activities — drives auto-progress when populated */}
              <PhasesEditor
                phases={statusForm.phases}
                onChange={(next) => setStatusForm((p) => ({ ...p, phases: next }))}
                showProgress={true}
                testidPrefix="edit-phase"
              />

              {/* Manual progress fallback (only used when no phases defined) */}
              {(statusForm.phases || []).length === 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Manual Progress: {statusForm.progress}%</label>
                  <input
                    type="range" min="0" max="100" step="5"
                    value={statusForm.progress}
                    onChange={(e) => setStatusForm((p) => ({ ...p, progress: Number(e.target.value) }))}
                    className="mt-2 w-full accent-[#1a237e]"
                    data-testid="progress-slider"
                  />
                  <p className="text-xs text-gray-500 mt-1">Tip: add activities above to track progress per task with weighted % contributions.</p>
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-gray-700">Progress Notes / Remarks</label>
                <textarea
                  value={statusForm.remarks}
                  onChange={(e) => setStatusForm((p) => ({ ...p, remarks: e.target.value }))}
                  rows="3"
                  placeholder="What changed since the last update?"
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                  data-testid="status-remarks"
                />
              </div>

              {/* Existing documents */}
              {(statusProject.documents || []).length > 0 && (
                <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                  <p className="text-xs font-semibold text-gray-700 mb-2">Existing Documents</p>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {(statusProject.documents || []).map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between text-xs bg-white rounded px-2 py-1.5 border border-gray-100">
                        <a href={`${process.env.REACT_APP_BACKEND_URL}${doc.url}`} target="_blank" rel="noreferrer"
                           className="flex items-center gap-1 text-blue-700 hover:underline truncate flex-1">
                          <FileText size={12} /><span className="truncate">{doc.name}</span>
                        </a>
                        {canDelete && (
                          <button type="button" onClick={() => handleDeleteDoc(statusProject.id, doc)}
                                  disabled={docDeletingId === doc.id}
                                  className="text-rose-600 hover:bg-rose-50 p-1 rounded disabled:opacity-50">
                            {docDeletingId === doc.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* New documents to attach */}
              <div>
                <label className="text-sm font-medium text-gray-700">Attach Documents</label>
                <input
                  ref={statusDocsInputRef}
                  type="file"
                  multiple
                  onChange={queueStatusDocs}
                  className="hidden"
                  data-testid="status-doc-input"
                />
                <button type="button" onClick={() => statusDocsInputRef.current?.click()}
                        className="mt-1 w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-[#1a237e] hover:bg-indigo-50/50 flex items-center justify-center gap-2">
                  <Paperclip size={14} /> Pick files (PDF / image / Excel / docs — up to 25 MB each)
                </button>
                {statusDocs.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {statusDocs.map((f, idx) => (
                      <span key={idx} className="inline-flex items-center gap-1 text-xs bg-indigo-50 text-indigo-700 px-2 py-1 rounded">
                        <FileText size={12} /> {f.name}
                        <button type="button" onClick={() => removeQueuedDoc(idx, setStatusDocs)} className="hover:text-rose-600 ml-1"><X size={10} /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-2 bg-gray-50 rounded-b-2xl">
              <button type="button" onClick={() => setShowStatusModal(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button type="submit" disabled={savingStatus}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-60"
                      data-testid="status-save-btn">
                <Save size={16} /> {savingStatus ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Add Project Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="new-project-modal">
          <form onSubmit={handleAddProject} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gradient-to-r from-[#1a237e] to-[#283593] rounded-t-2xl">
              <h2 className="text-lg font-bold text-white">New Project</h2>
              <button type="button" onClick={() => setShowAddModal(false)} className="p-2 text-white/80 hover:bg-white/10 rounded-lg"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Project Name *</label>
                <input type="text" value={addForm.name} onChange={(e) => setAddForm((p) => ({ ...p, name: e.target.value }))}
                       required className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                       data-testid="project-name-input" />
                <DuplicateProjectWarning name={addForm.name} projects={projectDirectory.projects} testId="ongoing-project-duplicate-warning" />
                {projectDirectory.error && <p role="alert" className="text-sm text-red-700 mt-2" data-testid="ongoing-project-directory-error">{projectDirectory.error} <button type="button" onClick={projectDirectory.refresh} className="underline" data-testid="ongoing-project-directory-retry">Retry</button></p>}
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Description</label>
                <textarea value={addForm.description} onChange={(e) => setAddForm((p) => ({ ...p, description: e.target.value }))} rows="2"
                          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700">Project In-charge</label>
                  <input type="text" value={addForm.projectIncharge} onChange={(e) => setAddForm((p) => ({ ...p, projectIncharge: e.target.value }))}
                         className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Estimated Cost (₹)</label>
                  <input type="number" step="0.01" value={addForm.estimatedCost} onChange={(e) => setAddForm((p) => ({ ...p, estimatedCost: e.target.value }))}
                         className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700">Start Date</label>
                  <input type="date" value={addForm.startDate} onChange={(e) => setAddForm((p) => ({ ...p, startDate: e.target.value }))}
                         className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Deadline</label>
                  <input type="date" value={addForm.deadline} onChange={(e) => setAddForm((p) => ({ ...p, deadline: e.target.value }))}
                         className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
              </div>

              {/* Activities / Phases */}
              <PhasesEditor
                phases={addForm.phases}
                onChange={(next) => setAddForm((p) => ({ ...p, phases: next }))}
                showProgress={false}
                testidPrefix="new-phase"
              />

              {/* Document Upload */}
              <div>
                <label className="text-sm font-medium text-gray-700">Project Documents</label>
                <input ref={addDocsInputRef} type="file" multiple onChange={queueNewProjectDocs} className="hidden"
                       data-testid="new-project-doc-input" />
                <button type="button" onClick={() => addDocsInputRef.current?.click()}
                        className="mt-1 w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-[#1a237e] hover:bg-indigo-50/50 flex items-center justify-center gap-2">
                  <Paperclip size={14} /> Attach files (drawings, BOQs, contracts… up to 25 MB each)
                </button>
                {newProjectDocs.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {newProjectDocs.map((f, idx) => (
                      <span key={idx} className="inline-flex items-center gap-1 text-xs bg-indigo-50 text-indigo-700 px-2 py-1 rounded">
                        <FileText size={12} /> {f.name}
                        <button type="button" onClick={() => removeQueuedDoc(idx, setNewProjectDocs)} className="hover:text-rose-600 ml-1"><X size={10} /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-2 bg-gray-50 rounded-b-2xl">
              <button type="button" onClick={() => setShowAddModal(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button type="submit" disabled={submitting}
                      className="flex items-center gap-2 px-4 py-2 bg-[#1a237e] text-white rounded-lg hover:bg-[#283593] font-medium disabled:opacity-60"
                      data-testid="project-save-btn">
                <Save size={16} /> {submitting ? 'Saving…' : 'Create Project'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Photo Upload Modal */}
      {showUploadModal && uploadingProject && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">Upload Progress Photo</h2>
              <button onClick={() => { setShowUploadModal(false); setUploadCaption(''); }} className="p-2 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div onClick={() => fileInputRef.current?.click()}
                   className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-[#1a237e] hover:bg-indigo-50/50 transition-colors">
                <Upload size={40} className="mx-auto text-gray-400 mb-3" />
                <p className="text-sm text-gray-600 font-medium">Click to select photo</p>
                <p className="text-xs text-gray-400 mt-1">JPG, PNG, WebP up to 50MB</p>
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Caption (optional)</label>
                <input type="text" value={uploadCaption} onChange={(e) => setUploadCaption(e.target.value)}
                       placeholder="Describe this progress photo..."
                       className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1a237e]/20 focus:border-[#1a237e] outline-none" />
              </div>
              {uploading && (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a237e]"></div>
                  <span className="ml-3 text-sm text-gray-600">Uploading...</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      <ProjectHistoryModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        projectId={historyProject?.id}
        projectName={historyProject?.name}
      />
    </div>
  );
}

export default OngoingProjects;
